/**
 * Podpis maila per skrzynka (DECISIONS 2026-10-09 [S] „Podpis maila per skrzynka”): HTML podpisu wklejony
 * w Ustawienia → Integracje → skrzynka, zapisany w `integrations.config.signatureHtml` po sanitizacji po stronie
 * serwera. Ten sam kod buduje podgląd w przeglądarce (wynik = to, co zapisze serwer).
 *
 * Sanitizacja = lista dozwolonych: znaczniki table / tbody / tr / td / div / span / p / br / a / img / b / strong / i,
 * atrybuty style, width, height, alt, cellpadding, cellspacing, border, `href` tylko http(s) / mailto / tel,
 * `src` obrazka tylko https. Wynik jest budowany od nowa (każdy znacznik i atrybut zapisany przez nas, wartości
 * z encjami), więc nic spoza listy nie przejdzie: skrypty, style, ramki i formularze znikają razem z treścią,
 * zdarzenia (`on…`) i inne atrybuty są pomijane, `style` traci deklaracje z `url(`, `expression`, `@import`
 * i funkcjami innymi niż kolory. Skaner jednym przebiegiem (czas liniowy, bez wyrażeń z nawrotami).
 */

/** Najdłuższy HTML przyjmowany do sanitizacji (wklejony podpis). */
export const MAIL_SIGNATURE_INPUT_MAX_CHARS = 20_000;
/** Najdłuższy podpis po sanitizacji (konfiguracja integracji ma limit 8 KB JSON). */
export const MAIL_SIGNATURE_MAX_CHARS = 6_000;
/** Klucz w `integrations.config`. */
export const MAIL_SIGNATURE_CONFIG_KEY = 'signatureHtml';

export const MAIL_SIGNATURE_MESSAGES = {
  tooLong: `Podpis jest za długi (najwyżej ${MAIL_SIGNATURE_MAX_CHARS} znaków po oczyszczeniu).`,
  inputTooLong: `Wklejony HTML jest za długi (najwyżej ${MAIL_SIGNATURE_INPUT_MAX_CHARS} znaków).`,
  saved: 'Podpis zapisany',
  removed: 'Podpis usunięty',
  hint: 'Wklej HTML podpisu (np. z generatora podpisów). Skrypty, formularze i obce atrybuty usuniemy przy zapisie.',
  appliedNote: 'Podpis skrzynki zostanie dodany pod treścią przy wysyłce.',
} as const;

const ALLOWED_TAGS = new Set(['table', 'tbody', 'tr', 'td', 'div', 'span', 'p', 'br', 'a', 'img', 'b', 'strong', 'i']);
const VOID_TAGS = new Set(['br', 'img']);
/** Usuwane razem z treścią (także gdy niezamknięte — wtedy do końca wejścia). */
const DROP_WITH_CONTENT = new Set([
  'script',
  'style',
  'head',
  'title',
  'noscript',
  'template',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'svg',
  'math',
  'form',
  'select',
  'textarea',
  'button',
  'xmp',
  'noembed',
  'noframes',
  'plaintext',
  'video',
  'audio',
]);
const COMMON_ATTRS = new Set(['style', 'width', 'height', 'alt', 'cellpadding', 'cellspacing', 'border']);
const BLOCK_TAGS = new Set(['table', 'tbody', 'tr', 'div', 'p']);
const CSS_FUNCTIONS = new Set(['rgb', 'rgba', 'hsl', 'hsla']);

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  laquo: '«',
  raquo: '»',
  bdquo: '„',
  rdquo: '”',
  ldquo: '“',
  rsquo: '’',
  lsquo: '‘',
  middot: '·',
  bull: '•',
  copy: '©',
  reg: '®',
  trade: '™',
  euro: '€',
  shy: '',
  zwnj: '',
  zwj: '',
  colon: ':',
  sol: '/',
  lpar: '(',
  rpar: ')',
  tab: '\t',
  newline: '\n',
};

function decodeEntities(text: string): string {
  if (!text.includes('&')) return text;
  let out = '';
  let i = 0;
  while (i < text.length) {
    const amp = text.indexOf('&', i);
    if (amp < 0) {
      out += text.slice(i);
      break;
    }
    out += text.slice(i, amp);
    const semi = text.indexOf(';', amp + 1);
    const body = semi > amp && semi - amp <= 12 ? text.slice(amp + 1, semi) : null;
    let decoded: string | null = null;
    if (body) {
      if (body[0] === '#') {
        const hex = body[1] === 'x' || body[1] === 'X';
        const digits = hex ? body.slice(2) : body.slice(1);
        if (digits && (hex ? /^[0-9a-f]+$/i : /^[0-9]+$/).test(digits)) {
          const code = Number.parseInt(digits, hex ? 16 : 10);
          const valid = code > 0 && code <= 0x10ffff && !(code >= 0xd800 && code <= 0xdfff);
          decoded = valid && (code >= 0x20 || code === 0x09 || code === 0x0a) ? String.fromCodePoint(code) : '';
        }
      } else if (/^[a-z][a-z0-9]*$/i.test(body)) {
        decoded = NAMED_ENTITIES[body.toLowerCase()] ?? null;
      }
    }
    if (decoded === null) {
      out += '&';
      i = amp + 1;
    } else {
      out += decoded;
      i = (semi as number) + 1;
    }
  }
  return out;
}

function escapeText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(text: string): string {
  return escapeText(text).replace(/"/g, '&quot;');
}

function isNameChar(c: string): boolean {
  return /[a-z0-9:_-]/i.test(c);
}

interface Attr {
  name: string;
  value: string;
}

/** Atrybuty z wnętrza znacznika (bez nazwy), jednym przebiegiem. */
function parseAttrs(src: string): Attr[] {
  const out: Attr[] = [];
  let i = 0;
  const n = src.length;
  while (i < n) {
    while (i < n && (/\s/.test(src[i] as string) || src[i] === '/')) i++;
    if (i >= n) break;
    const start = i;
    while (i < n && !/[\s=/>]/.test(src[i] as string)) i++;
    const name = src.slice(start, i).toLowerCase();
    if (!name) {
      i++;
      continue;
    }
    while (i < n && /\s/.test(src[i] as string)) i++;
    let value = '';
    if (src[i] === '=') {
      i++;
      while (i < n && /\s/.test(src[i] as string)) i++;
      const q = src[i];
      if (q === '"' || q === "'") {
        const end = src.indexOf(q, i + 1);
        value = src.slice(i + 1, end < 0 ? n : end);
        i = end < 0 ? n : end + 1;
      } else {
        const vs = i;
        while (i < n && !/[\s>]/.test(src[i] as string)) i++;
        value = src.slice(vs, i);
      }
    }
    out.push({ name, value: decodeEntities(value) });
  }
  return out;
}

/** Adres bez znaków sterujących i odstępów (przeglądarki je ignorują — `jav\tascript:` to nadal `javascript:`). */
function compactUrl(raw: string): string {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: celowo usuwamy znaki sterujące z adresu
  return raw.replace(/[\u0000- \u007f-\u009f]+/g, '');
}

export function safeSignatureHref(raw: string): string | null {
  const v = compactUrl(raw);
  if (/^https?:\/\/[^/]/i.test(v)) return v;
  if (/^mailto:[^/]/i.test(v)) return v;
  if (/^tel:\+?[0-9().-]+$/i.test(v)) return v;
  return null;
}

export function safeSignatureImageSrc(raw: string): string | null {
  const v = compactUrl(raw);
  return /^https:\/\/[^/]/i.test(v) ? v : null;
}

/** `style` tylko z bezpiecznymi deklaracjami (właściwość `a-z-`, wartość bez url(), expression, @import, `\`, `<`). */
export function sanitizeStyle(raw: string): string {
  const out: string[] = [];
  for (const decl of raw.split(';')) {
    const colon = decl.indexOf(':');
    if (colon < 0) continue;
    const prop = decl.slice(0, colon).trim().toLowerCase();
    const value = decl.slice(colon + 1).trim();
    if (!/^-?[a-z][a-z-]{0,40}$/.test(prop) || !value || value.length > 300) continue;
    if (prop === 'behavior' || prop.startsWith('-moz-binding')) continue;
    const lower = value.toLowerCase();
    if (/[\\<>"{}@]/.test(value) || lower.includes('expression') || lower.includes('javascript')) continue;
    if (!/^[\p{L}\p{N} #%.,'()!/_+-]+$/u.test(value)) continue;
    let fnOk = true;
    for (const m of lower.matchAll(/([a-z-]*)\s*\(/g)) {
      if (!CSS_FUNCTIONS.has(m[1] as string)) fnOk = false;
    }
    if (!fnOk) continue;
    out.push(`${prop}:${value}`);
  }
  return out.join(';');
}

function attrsFor(tag: string, attrs: Attr[]): string {
  const parts: string[] = [];
  const seen = new Set<string>();
  for (const a of attrs) {
    if (seen.has(a.name)) continue;
    let value: string | null = null;
    if (COMMON_ATTRS.has(a.name)) {
      if (a.name === 'style') {
        const s = sanitizeStyle(a.value);
        value = s || null;
      } else if (a.name === 'alt') {
        value = a.value.slice(0, 200);
      } else {
        value = /^[0-9]{1,4}(%|px)?$/i.test(a.value.trim()) ? a.value.trim() : null;
      }
    } else if (tag === 'a' && a.name === 'href') {
      value = safeSignatureHref(a.value);
    } else if (tag === 'img' && a.name === 'src') {
      value = safeSignatureImageSrc(a.value);
    }
    if (value === null) continue;
    seen.add(a.name);
    parts.push(` ${a.name}="${escapeAttr(value)}"`);
  }
  if (tag === 'a' && seen.has('href')) parts.push(' target="_blank" rel="noopener noreferrer"');
  return parts.join('');
}

type Token =
  | { kind: 'text'; text: string }
  | { kind: 'open'; tag: string; attrs: Attr[]; selfClosing: boolean }
  | { kind: 'close'; tag: string };

/** Skaner HTML jednym przebiegiem: tekst, znaczniki otwierające i zamykające (komentarze, doctype i treść usuwanych pomija). */
function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  const n = input.length;
  const lower = input.toLowerCase();
  let i = 0;
  let text = '';
  const flush = () => {
    if (text) tokens.push({ kind: 'text', text: decodeEntities(text) });
    text = '';
  };
  while (i < n) {
    const lt = input.indexOf('<', i);
    if (lt < 0) {
      text += input.slice(i);
      break;
    }
    text += input.slice(i, lt);
    i = lt;
    if (input.startsWith('<!--', i)) {
      const end = input.indexOf('-->', i + 4);
      i = end < 0 ? n : end + 3;
      continue;
    }
    const next = input[i + 1] ?? '';
    if (next === '!' || next === '?') {
      const end = input.indexOf('>', i + 2);
      i = end < 0 ? n : end + 1;
      continue;
    }
    const closing = next === '/';
    let j = i + (closing ? 2 : 1);
    const nameStart = j;
    while (j < n && isNameChar(input[j] as string)) j++;
    const tag = lower.slice(nameStart, j);
    if (!tag || !/^[a-z]/.test(tag)) {
      // „<” bez znacznika = zwykły znak tekstu
      text += '<';
      i += 1;
      continue;
    }
    // koniec znacznika: pierwszy „>” poza cudzysłowem
    let k = j;
    let quote: string | null = null;
    while (k < n) {
      const c = input[k] as string;
      if (quote) {
        if (c === quote) quote = null;
      } else if (c === '"' || c === "'") quote = c;
      else if (c === '>') break;
      k++;
    }
    const inner = input.slice(j, Math.min(k, n));
    i = k < n ? k + 1 : n;
    flush();
    if (closing) {
      tokens.push({ kind: 'close', tag });
      continue;
    }
    if (DROP_WITH_CONTENT.has(tag)) {
      // treść do zamknięcia tego samego znacznika (brak zamknięcia = do końca)
      const end = lower.indexOf(`</${tag}`, i);
      if (end < 0) {
        i = n;
      } else {
        const gt = input.indexOf('>', end);
        i = gt < 0 ? n : gt + 1;
      }
      continue;
    }
    // „/>” zamyka element tylko po odstępie, cudzysłowie albo samej nazwie (`href=https://x.pl/>` to wartość z „/”)
    const trimmed = inner.trimEnd();
    const selfClosing = trimmed.endsWith('/') && (trimmed.length === 1 || /[\s"']/.test(trimmed.at(-2) as string));
    tokens.push({ kind: 'open', tag, attrs: parseAttrs(inner), selfClosing });
  }
  flush();
  return tokens;
}

/**
 * Oczyszczony HTML podpisu (pusty wynik = brak podpisu). Znaczniki spoza listy znikają (ich tekst zostaje), znaczniki
 * są zawsze domknięte we właściwej kolejności.
 */
export function sanitizeSignatureHtml(input: string): string {
  const tokens = tokenize(input.slice(0, MAIL_SIGNATURE_INPUT_MAX_CHARS));
  let out = '';
  const stack: string[] = [];
  for (const t of tokens) {
    if (t.kind === 'text') {
      out += escapeText(t.text);
    } else if (t.kind === 'open') {
      if (!ALLOWED_TAGS.has(t.tag)) continue;
      const attrs = attrsFor(t.tag, t.attrs);
      // obraz bez bezpiecznego adresu (http, data:, javascript:) = brak obrazu
      if (t.tag === 'img' && !attrs.includes(' src=')) continue;
      out += `<${t.tag}${attrs}>`;
      // `<div/>` = pusty element (zamknięty od razu), inaczej zamknięcie z treścią lub na końcu
      if (!VOID_TAGS.has(t.tag)) {
        if (t.selfClosing) out += `</${t.tag}>`;
        else stack.push(t.tag);
      }
    } else {
      if (!ALLOWED_TAGS.has(t.tag) || VOID_TAGS.has(t.tag)) continue;
      const at = stack.lastIndexOf(t.tag);
      if (at < 0) continue;
      while (stack.length > at) out += `</${stack.pop()}>`;
    }
  }
  while (stack.length) out += `</${stack.pop()}>`;
  // podpis bez treści (same puste znaczniki i odstępy) = brak podpisu
  if (!signatureHtmlToText(out) && !/<img\s/i.test(out)) return '';
  return out.trim();
}

/** Tekstowa wersja podpisu (część text/plain maila): bloki = nowe linie, linki = sam tekst, obrazy pomijane. */
export function signatureHtmlToText(html: string): string {
  const tokens = tokenize(html);
  let out = '';
  for (const t of tokens) {
    if (t.kind === 'text') out += t.text.replace(/\s+/g, ' ');
    else if (t.tag === 'br') out += '\n';
    else if (BLOCK_TAGS.has(t.tag)) out += '\n';
    else if (t.kind === 'open' && t.tag === 'td') out += ' ';
  }
  return out
    .replace(/ /g, ' ')
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .filter((line) => line !== '')
    .join('\n')
    .trim();
}

/** Treść maila (tekst osoby) jako prosty HTML: encje, nowe linie = `<br>`. */
export function plainTextToHtml(text: string): string {
  return escapeText(text.replace(/\r\n?/g, '\n')).replace(/\n/g, '<br>');
}

/** Separator podpisu w części tekstowej (konwencja „-- ” — programy pocztowe rozpoznają podpis). */
export const MAIL_SIGNATURE_TEXT_SEPARATOR = '\n\n-- \n';

/**
 * Treść wysyłki z podpisem skrzynki: część tekstowa (treść + podpis jako tekst) i HTML (treść jako prosty HTML +
 * podpis). Bez podpisu — sama część tekstowa (jak dotąd).
 */
export function composeMailWithSignature(
  bodyText: string,
  signatureHtml: string | null | undefined,
): { bodyText: string; bodyHtml?: string } {
  const signature = signatureHtml ? sanitizeSignatureHtml(signatureHtml) : '';
  if (!signature) return { bodyText };
  const body = bodyText.replace(/\s+$/, '');
  const sigText = signatureHtmlToText(signature);
  const text = sigText ? `${body}${MAIL_SIGNATURE_TEXT_SEPARATOR}${sigText}` : body;
  const html = [
    '<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>',
    `<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:20px">${plainTextToHtml(body)}</div>`,
    `<div style="padding-top:16px">${signature}</div>`,
    '</body></html>',
  ].join('');
  return { bodyText: text, bodyHtml: html };
}

/** Podpis z konfiguracji integracji skrzynki (oczyszczony ponownie; brak albo pusty = `null`). */
export function mailboxSignatureOf(config: Record<string, unknown> | null | undefined): string | null {
  const raw = config?.[MAIL_SIGNATURE_CONFIG_KEY];
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const clean = sanitizeSignatureHtml(raw);
  return clean && clean.length <= MAIL_SIGNATURE_MAX_CHARS ? clean : null;
}
